/**
 * Mini oynatıcı — rota değişse de görünen alt şerit (Tasarım V4, Öneri 7).
 *
 * Tam ekran "Şimdi Çalıyor" yüzeyine geçiş için başlık/kapak alanı tıklanır.
 * Asgari kontrol: oynat/duraklat, sonraki, kapat. Ses/hız ayarları tam
 * ekran yüzeyde.
 */
import type { ReactNode } from 'react';
import { formatDuration } from '../format.ts';
import { CloseIcon, MusicIcon, PauseIcon, PlayIcon, QueueIcon, SkipNextIcon } from '../Icon.tsx';
import { resizeProxyImage } from '../imageSize.ts';
import { mediaCopy } from '../mediaCopy.ts';
import { mediaUrl } from '../mediaToken.ts';
import { monogramGradient } from '../monogram.ts';
import type { AudioPlayerApi } from '../useAudioPlayer.ts';

export function MiniPlayer({
  player,
  onExpand,
  onOpenQueue,
}: {
  player: AudioPlayerApi;
  onExpand: () => void;
  onOpenQueue: () => void;
}): ReactNode {
  const { current } = player;
  if (current === null) return null;
  const copy = mediaCopy('music');

  const percent =
    player.durationMs > 0
      ? Math.min(100, Math.max(0, (player.positionMs / player.durationMs) * 100))
      : 0;

  const subtitle = player.error
    ? player.error
    : player.isBuffering
      ? 'Yükleniyor…'
      : player.needsGesture
        ? copy.playHint
        : `${formatDuration(player.positionMs)} / ${formatDuration(player.durationMs)}`;

  /*
   * Albüm kapağı parçanın gömülü kapağından önce gelir; yüklenen/klasör
   * kapağı varsa yer tutucu nota gizlenir.
   */
  const albumArtwork =
    current.albumCoverUrl ??
    resizeProxyImage(current.posterUrl, 'w185') ??
    current.thumbnailUrl ??
    null;
  const artwork = albumArtwork ?? mediaUrl(`/api/items/${current.id}/thumbnail`);

  return (
    <section className="mini-player" aria-label="Şimdi çalıyor">
      <div className="mini-player__progress" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </div>

      <button type="button" className="mini-player__info" onClick={onExpand}>
        <span
          className="mini-player__cover"
          style={{
            backgroundImage: `url(${artwork}), ${monogramGradient(current.id)}`,
          }}
          aria-hidden="true"
        >
          {albumArtwork === null && <MusicIcon size={16} />}
        </span>
        <span className="mini-player__text">
          <span className="mini-player__title">{current.title}</span>
          <span className="mini-player__sub">{subtitle}</span>
        </span>
      </button>

      <div className="mini-player__actions">
        <button
          type="button"
          className="mini-player__btn"
          onClick={player.toggle}
          aria-label={player.isPlaying ? 'Duraklat' : copy.play}
          title={player.isPlaying ? 'Duraklat' : copy.play}
        >
          {player.isPlaying ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
        </button>
        <button
          type="button"
          className="mini-player__btn"
          onClick={player.next}
          aria-label="Sonraki parça"
          title="Sonraki parça"
        >
          <SkipNextIcon size={18} />
        </button>
        <button
          type="button"
          className="mini-player__btn"
          onClick={onOpenQueue}
          aria-label="Çalma sırasını aç"
          title="Çalma sırası"
        >
          <QueueIcon size={18} />
        </button>
        <button
          type="button"
          className="mini-player__btn mini-player__btn--close"
          onClick={player.close}
          aria-label="Çalmayı kapat"
          title="Çalmayı kapat"
        >
          <CloseIcon size={16} />
        </button>
      </div>
    </section>
  );
}
